import {
    URLSearchParamsInit,
    useSearchParams as useSearchParamsRRD,
} from 'react-router-dom';

/**
 * A wrapper hook that allows to read and write search params.
 * @param keys array of params that have scalar values
 * @param keysArray array of params that have an array value
 */
export const useSearchParams = <
    K extends string = string,
    KArray extends string = string,
>(
    keys: K[],
    keysArray: KArray[] = [],
    defaultInit?: URLSearchParamsInit,
): { [k in K]: readonly [string | null, (v: string) => void] } & {
    [k in KArray]: readonly [string[], (v: string[]) => void];
} => {
    const [searchParams, setSearchParams] = useSearchParamsRRD(defaultInit);

    const buildScalarSetSearchParam = (key: string) => (value: string) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (value === null || value === '') {
            newSearchParams.delete(key);
        } else {
            newSearchParams.set(key, value);
        }
        setSearchParams(newSearchParams);
    };
    const buildArraySetSearchParam = (key: string) => (value: string[]) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete(key);
        value.forEach((v) => {
            newSearchParams.append(key, v);
        });
        setSearchParams(newSearchParams);
    };

    const scalarParamsAndSetters = Object.fromEntries(
        keys.map((k) => [
            k,
            [searchParams.get(k), buildScalarSetSearchParam(k)] as const,
        ]),
    );

    const arrayParamsAndSetters = Object.fromEntries(
        keysArray.map((k) => [
            k,
            [searchParams.getAll(k), buildArraySetSearchParam(k)] as const,
        ]),
    );

    return {
        ...scalarParamsAndSetters,
        ...arrayParamsAndSetters,
    };
};
